<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 mb-4">
    <!-- 标题行 -->
    <div class="flex justify-between items-start mb-4">
      <div class="flex items-center gap-3 flex-1">
        <!-- 状态标签 -->
        <span :class="getStatusBadgeClass(announcement.status)" class="px-2 py-1 text-xs font-medium rounded">
          {{ AnnouncementStatusLabel[announcement.status] }}
        </span>
        <!-- 优先级标签 -->
        <span :class="getPriorityBadgeClass(announcement.priority)" class="px-2 py-1 text-xs font-medium rounded">
          {{ AnnouncementPriorityLabel[announcement.priority] }}
        </span>
        <!-- 弹窗标签 -->
        <span v-if="announcement.isPopup"
          class="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
          🔔 弹窗公告
        </span>
        <span v-else
          class="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
          📋 普通公告
        </span>
        <!-- 标题 -->
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {{ announcement.title }}
        </h3>
      </div>
      <span class="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap ml-4">
        {{ formatDate(announcement.createdAt) }}
      </span>
    </div>

    <!-- 有效期 -->
    <div v-if="announcement.validFrom || announcement.validUntil"
      class="flex gap-4 mb-3 text-sm text-gray-600 dark:text-gray-400">
      <span v-if="announcement.validFrom">
        📅 生效时间: {{ formatDate(announcement.validFrom) }}
      </span>
      <span v-if="announcement.validUntil">
        ⏰ 失效时间: {{ formatDate(announcement.validUntil) }}
      </span>
    </div>

    <!-- 内容预览 -->
    <div class="bg-gray-50 dark:bg-gray-700 rounded p-3 mb-4">
      <p class="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
        {{ getContentPreview(announcement.content) }}
      </p>
    </div>

    <!-- 操作按钮 -->
    <div class="flex gap-2">
      <button @click="handleView"
        class="cancel-btn text-sm font-medium">
        查看详情
      </button>
      <button @click="handlePreview"
        class="cancel-btn text-sm font-medium">
        预览
      </button>
      <button @click="handleEdit"
        class="primary-btn text-sm font-medium">
        编辑
      </button>
      <!-- 发布/撤回按钮 -->
      <button v-if="announcement.status === AnnouncementStatus.DRAFT" @click="handlePublish"
        class="primary-btn text-sm font-medium">
        发布
      </button>
      <button v-else @click="handleUnpublish"
        class="warning-btn text-sm font-medium">
        撤回
      </button>
      <button @click="handleDelete"
        class="danger-btn text-sm font-medium">
        删除
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AnnouncementStatus, AnnouncementStatusLabel, AnnouncementPriorityLabel, AnnouncementPriority } from '@/types/announcement'
import type { Announcement } from '@/types/announcement'
import { formatDate } from '@/utils/date'

/**
 * 组件属性
 */
interface Props {
  /** 公告数据 */
  announcement: Announcement
}

/**
 * 组件事件
 */
interface Emits {
  /** 查看详情事件 */
  (e: 'view', announcement: Announcement): void
  /** 预览事件 */
  (e: 'preview', announcement: Announcement): void
  /** 编辑事件 */
  (e: 'edit', announcement: Announcement): void
  /** 发布事件 */
  (e: 'publish', announcement: Announcement): void
  /** 撤回事件 */
  (e: 'unpublish', announcement: Announcement): void
  /** 删除事件 */
  (e: 'delete', announcement: Announcement): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

/**
 * 获取状态徽章样式类
 */
const getStatusBadgeClass = (status: AnnouncementStatus): string => {
  const classMap = {
    [AnnouncementStatus.DRAFT]: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    [AnnouncementStatus.PUBLISHED]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  }
  return classMap[status] || classMap[AnnouncementStatus.DRAFT]
}

/**
 * 获取优先级徽章样式类
 */
const getPriorityBadgeClass = (priority: AnnouncementPriority): string => {
  const classMap = {
    [AnnouncementPriority.HIGH]: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    [AnnouncementPriority.NORMAL]: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    [AnnouncementPriority.LOW]: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  }
  return classMap[priority] || classMap[AnnouncementPriority.NORMAL]
}

/**
 * 获取内容预览 (去除 HTML 标签, 截取前 100 字符)
 */
const getContentPreview = (content: string): string => {
  const text = content.replace(/<[^>]*>/g, '')
  return text.length > 100 ? text.substring(0, 100) + '...' : text
}

/**
 * 查看详情
 */
const handleView = () => {
  emit('view', props.announcement)
}

/**
 * 预览
 */
const handlePreview = () => {
  emit('preview', props.announcement)
}

/**
 * 编辑
 */
const handleEdit = () => {
  emit('edit', props.announcement)
}

/**
 * 发布
 */
const handlePublish = () => {
  emit('publish', props.announcement)
}

/**
 * 撤回
 */
const handleUnpublish = () => {
  emit('unpublish', props.announcement)
}

/**
 * 删除
 */
const handleDelete = () => {
  emit('delete', props.announcement)
}
</script>
